import { useEffect, useRef } from 'react';
import styles from './InstagramEmbed.module.scss';

interface IInstagramEmbedProps {
  videoId: string;
  iframeTitle: string;
  videoUrl?: string;
}

function tryExtractInstagramEmbedData(value: string): { type: 'reel' | 'p'; id: string } | null {
  try {
    const parsedUrl = new URL(value);
    const segments = parsedUrl.pathname.split('/').filter(Boolean);
    const contentTypeIndex = segments.findIndex(
      (segment) => segment === 'reel' || segment === 'reels' || segment === 'p'
    );

    if (contentTypeIndex >= 0 && segments[contentTypeIndex + 1]) {
      const type = segments[contentTypeIndex] === 'p' ? 'p' : 'reel';
      return { type, id: segments[contentTypeIndex + 1] };
    }
  } catch {
    // Not a URL, ignore
  }

  return null;
}

function getInstagramEmbedSrc(videoId: string, videoUrl?: string) {
  if (videoUrl) {
    const extractedFromVideoUrl = tryExtractInstagramEmbedData(videoUrl);
    if (extractedFromVideoUrl) {
      return `https://www.instagram.com/${extractedFromVideoUrl.type}/${extractedFromVideoUrl.id}/embed/`;
    }
  }

  const extractedFromVideoId = tryExtractInstagramEmbedData(videoId);
  if (extractedFromVideoId) {
    return `https://www.instagram.com/${extractedFromVideoId.type}/${extractedFromVideoId.id}/embed/`;
  }

  return `https://www.instagram.com/reel/${videoId}/embed/`;
}

export default function InstagramEmbed({ videoId, iframeTitle, videoUrl }: IInstagramEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    containerRef?.current?.focus();
  });

  // Load Instagram embed SDK if available
  useEffect(() => {
    if (typeof window !== 'undefined' && (window as Record<string, unknown>).instgrm) {
      (
        (window as Record<string, unknown>).instgrm as { Embeds: { process: () => void } }
      ).Embeds.process();
    }
  }, [videoId]);

  return (
    <div className={styles.instagramEmbedWrapper}>
      <div
        className={styles.iFrameContainerInstagram}
        role="region"
        aria-label={iframeTitle}
        ref={containerRef}
        tabIndex={-1}
      >
        <iframe
          title={iframeTitle}
          src={getInstagramEmbedSrc(videoId, videoUrl)}
          scrolling="no"
          allowFullScreen
          frameBorder="0"
          sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts allow-top-navigation allow-same-origin"
        />
      </div>
    </div>
  );
}
